import React from 'react';
import { plainToInstance } from 'class-transformer';
import {
  IMonitoringPromptRunCitationRuntime,
  IMonitoringPromptRunMentionRuntime,
  MonitoringPromptKpi,
  MonitoringPromptRunParsed,
  MonitoringPromptRunStatus,
  SyncCoreDataDefinitionsEnumTranslator,
  TaxonomyFacet,
} from '@edgebox/sync-core-data-definitions';
import { ClientMonitoringPromptRunEntity, ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { TaxonomyTermNameBadge } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import { MarkdownContentBox } from './MarkdownContentBox';
import { isKpiShown, PurposeKpiMask } from './purposeKpiMask.utils';
import { MENTION_TONE_LABEL, TONE_KPI, TONE_METRIC_LABEL, toneValue } from '../toneMetric';

type ParsedContentTab = 'response' | 'attribution' | 'aspects' | 'evidence' | 'citations';

/**
 * Parsed runs at this compatibility date (or later) carry Attribution v2 unit-level data.
 * Older (v1) runs only expose the response/mentions/citations tabs.
 *
 * This must match the stamp Sync Core writes onto v2 parsed runs (`2026-08-27`). If it drifts,
 * date-based detection silently stops matching new runs and only the structural fallback below
 * still works. `@edgebox/sync-core-data-definitions` exposes no constant for this stamp yet, so
 * it is pinned here rather than imported.
 */
const V2_COMPATIBILITY_DATE = '2026-08-27';

function isV2ParsedRun(parsed?: MonitoringPromptRunParsed): boolean {
  if (!parsed) {
    return false;
  }

  // YYYY-MM-DD strings compare chronologically.
  if (typeof parsed.compatibilityDate === 'string' && parsed.compatibilityDate >= V2_COMPATIBILITY_DATE) {
    return true;
  }

  return !!(parsed.units?.length || parsed.resolution?.length || parsed.aspects?.length || parsed.explicitRecommendation);
}

/** Human-readable label for a closed-set enum value like `name-card` → `Name card`. */
function prettifyEnum(value?: string): string {
  if (!value) {
    return '-';
  }

  const spaced = value.replace(/[-_]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The stable taxonomy-term key of a (serialized or runtime) revision reference. */
function referenceKey(reference: unknown): string {
  if (typeof reference === 'string') {
    return reference.trim();
  }

  if (!reference || typeof reference !== 'object') {
    return '';
  }

  const record = reference as { getStableKey?: () => string; key?: unknown };
  return String(record.getStableKey?.() || record.key || '').trim();
}

function formatScore(value?: number): string {
  return typeof value === 'number' && Number.isFinite(value) ? value.toFixed(2) : '-';
}

function formatDate(value?: { format: (template: string) => string }) {
  if (!value) {
    return '-';
  }

  return value.format('MMM DD, HH:mm');
}

function statusClass(status?: MonitoringPromptRunStatus) {
  if (status === MonitoringPromptRunStatus.Processed) {
    return 'bg-emerald-50 border-emerald-200 text-emerald-700';
  }

  if (status === MonitoringPromptRunStatus.Failed) {
    return 'bg-rose-50 border-rose-200 text-rose-700';
  }

  if (status === MonitoringPromptRunStatus.Fetched) {
    return 'bg-sky-50 border-sky-200 text-sky-700';
  }

  return 'bg-slate-100 border-slate-200 text-slate-600';
}

function translateRunStatus(status: MonitoringPromptRunStatus): string {
  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptRunStatus', status as any);
  } catch {
    return status;
  }
}

export function MonitoringPromptRunStatusBadge({ status }: { status: MonitoringPromptRunStatus }) {
  return (
    <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-bold ${statusClass(status)}`}>
      {translateRunStatus(status)}
    </span>
  );
}

function toObject(value: unknown): Record<string, unknown> | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return value as Record<string, unknown>;
}

function extractResponseMarkdown(parsedPayload: Record<string, unknown>): string {
  const rootCandidates = [parsedPayload.response, parsedPayload.responseMarkdown, parsedPayload.markdownResponse, parsedPayload.markdown];

  for (const candidate of rootCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  const output = toObject(parsedPayload.output);
  if (!output) {
    return '';
  }

  const outputCandidates = [output.response, output.responseMarkdown, output.markdownResponse, output.markdown, output.answer];
  for (const candidate of outputCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate;
    }
  }

  return '';
}

function renderFacet(facet?: TaxonomyFacet): string {
  if (!facet) {
    return '-';
  }

  try {
    return SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyFacet', facet);
  } catch {
    return facet;
  }
}

function getReferenceStableKey(reference: unknown): string {
  if (!reference || typeof reference !== 'object') {
    return '';
  }

  const referenceRecord = reference as {
    getStableKey?: () => string;
    key?: unknown;
  };

  return String(referenceRecord.getStableKey?.() || referenceRecord.key || '').trim();
}

/** Renders a taxonomy-term revision reference as a resolved name badge (or a dash). */
function TermRefBadge({ api, reference }: { api?: ISyncCoreApi; reference: unknown }) {
  const key = referenceKey(reference);
  if (!key) {
    return <span className="text-slate-400">-</span>;
  }

  return <TaxonomyTermNameBadge api={api} termKey={key} />;
}

/**
 * The technical view of one run: what was extracted, and the parsed payload behind it.
 *
 * It reads the SAME measurements the screens around it render as KPIs, so it obeys the same purpose
 * mask. A brand probe whose sidebar has dropped the position card and the share bars cannot then
 * print `#1` and `Share 99.2%` for every entity one click further down — the two would contradict
 * each other on one screen, and the more technical of the two would win the reader's belief.
 *
 * What the mask does NOT touch here is everything that is not a measurement: the entity names and
 * match types, the response markdown, the unit-level attribution, the raw evidence rows and their
 * ordering, the citations and the run-quality counters. Those describe how the answer was read, not
 * how the brand scored, and a purpose that fixes a KPI says nothing about them.
 */
export function MonitoringPromptRunDetail({
  run,
  api,
  kpiMask,
}: {
  run?: ClientMonitoringPromptRunEntity;
  api?: ISyncCoreApi;
  /**
   * Which KPIs the purpose in view measures.
   *
   * Required, and with no default on purpose — the same rule `EntityShareAndSentiment.showShare`
   * follows. A defaulted mask is how this component came to render an unmasked `#1` and a share
   * percentage under a screen that had already suppressed both. A call site with no purpose in view
   * passes `NO_PURPOSE_KPI_MASK`, which is today's behaviour stated rather than inherited.
   */
  kpiMask: PurposeKpiMask;
}) {
  const [activeTab, setActiveTab] = React.useState<ParsedContentTab>('response');
  const [responseMarkdown, setResponseMarkdown] = React.useState<string>('');
  const [mentions, setMentions] = React.useState<IMonitoringPromptRunMentionRuntime[]>([]);
  const [citations, setCitations] = React.useState<IMonitoringPromptRunCitationRuntime[]>([]);
  const [parsed, setParsed] = React.useState<MonitoringPromptRunParsed | undefined>(undefined);
  const [parsedPayloadError, setParsedPayloadError] = React.useState<string | undefined>(undefined);
  const [parsedPayloadLoading, setParsedPayloadLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    setActiveTab('response');

    if (!run?.parsedFile?.get) {
      setResponseMarkdown('');
      setMentions([]);
      setCitations([]);
      setParsed(undefined);
      setParsedPayloadError(undefined);
      setParsedPayloadLoading(false);
      return;
    }

    let active = true;
    setParsedPayloadLoading(true);
    setParsedPayloadError(undefined);

    run.parsedFile
      .get()
      .then(async (file) => {
        const downloadUrl = file?.downloadUrl;
        if (!downloadUrl) {
          if (!active) {
            return;
          }

          setResponseMarkdown('');
          setMentions([]);
          setCitations([]);
          setParsed(undefined);
          setParsedPayloadError('Parsed run payload is not available.');
          setParsedPayloadLoading(false);
          return;
        }

        const response = await fetch(downloadUrl);
        const content = await response.text();

        if (!active) {
          return;
        }

        let parsedUnknown: unknown;
        try {
          parsedUnknown = JSON.parse(content);
        } catch {
          setResponseMarkdown('');
          setMentions([]);
          setCitations([]);
          setParsed(undefined);
          setParsedPayloadError('Parsed run payload is invalid JSON.');
          setParsedPayloadLoading(false);
          return;
        }

        const parsedPayloadRoot = toObject(parsedUnknown);
        if (!parsedPayloadRoot) {
          setResponseMarkdown('');
          setMentions([]);
          setCitations([]);
          setParsed(undefined);
          setParsedPayloadError('Parsed run payload has an invalid format.');
          setParsedPayloadLoading(false);
          return;
        }

        const parsedRuntime = plainToInstance(MonitoringPromptRunParsed, parsedPayloadRoot);

        setResponseMarkdown(parsedRuntime.markdown || extractResponseMarkdown(parsedPayloadRoot));
        setMentions(parsedRuntime.mentions ?? []);
        setCitations(parsedRuntime.citations ?? []);
        setParsed(parsedRuntime);

        setParsedPayloadError(undefined);
        setParsedPayloadLoading(false);
      })
      .catch(() => {
        if (!active) {
          return;
        }

        setResponseMarkdown('');
        setMentions([]);
        setCitations([]);
        setParsed(undefined);
        setParsedPayloadError('Failed to load parsed run payload.');
        setParsedPayloadLoading(false);
      });

    return () => {
      active = false;
    };
  }, [run]);

  const isV2 = isV2ParsedRun(parsed);
  const units = parsed?.units ?? [];
  const labelsByUnit = React.useMemo(() => {
    const map = new Map<string, NonNullable<MonitoringPromptRunParsed['labels']>[number]>();
    for (const label of parsed?.labels ?? []) {
      map.set(label.unitId, label);
    }
    return map;
  }, [parsed]);
  const resolutionByUnit = React.useMemo(() => {
    const map = new Map<string, NonNullable<MonitoringPromptRunParsed['resolution']>>();
    for (const record of parsed?.resolution ?? []) {
      const list = map.get(record.unitId) ?? [];
      list.push(record);
      map.set(record.unitId, list);
    }
    return map;
  }, [parsed]);
  const aspects = parsed?.aspects ?? [];
  const explicitRecommendation = parsed?.explicitRecommendation ?? undefined;
  const qualityMetrics = parsed?.qualityMetrics;

  if (!run) {
    return <div className="text-sm text-slate-500">No run selected.</div>;
  }

  const engineKey = getReferenceStableKey(run.engine);

  const showsPosition = isKpiShown(kpiMask, MonitoringPromptKpi.Position);
  const showsShareOfVoice = isKpiShown(kpiMask, MonitoringPromptKpi.ShareOfVoice);
  const showsSentiment = isKpiShown(kpiMask, TONE_KPI);
  const showsRecommendation = isKpiShown(kpiMask, MonitoringPromptKpi.Recommendation);
  const showsAttributes = isKpiShown(kpiMask, MonitoringPromptKpi.Attributes);

  const tabs: { key: ParsedContentTab; label: string }[] = [
    { key: 'response', label: 'Answer' },
    ...(isV2 ? ([{ key: 'attribution', label: 'Attribution' }] as const) : []),
    // The aspect table is the Attributes KPI in its rawest form, so a purpose that does not measure
    // attributes loses the tab outright rather than opening onto an empty one.
    ...(isV2 && aspects.length && showsAttributes ? ([{ key: 'aspects', label: 'Aspects' }] as const) : []),
    { key: 'evidence', label: 'Evidence' },
    { key: 'citations', label: 'Citations' },
  ];
  const currentTab = tabs.some((tab) => tab.key === activeTab) ? activeTab : 'response';

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white p-4 ring-1 ring-inset ring-slate-200/80">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <MonitoringPromptRunStatusBadge status={run.status} />
          <span className="text-xs text-slate-500">Scheduled {formatDate(run.scheduledFor)}</span>
          <span className="text-xs text-slate-500">Computed {formatDate(run.computedAt)}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <div>
            <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Country</div>
            <div className="text-slate-800">
              {run.country ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptCountry', run.country) : '-'}
            </div>
          </div>
          <div>
            <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Location</div>
            <div className="text-slate-800">{run.location || '-'}</div>
          </div>
          <div>
            <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Engine</div>
            <div className="text-slate-800 flex items-center gap-1.5">
              {engineKey ? (
                <TaxonomyTermNameBadge api={api} termKey={engineKey} applyFacetPresentation termFacet={TaxonomyFacet.Engine} />
              ) : (
                <span>-</span>
              )}
              {run.engineModel ? <span className="text-slate-500">({run.engineModel})</span> : null}
            </div>
          </div>
          <div>
            <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Entities</div>
            <div className="text-slate-800">{run.entities?.length ?? 0}</div>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl bg-white ring-1 ring-inset ring-slate-200/80">
        <div className="px-4 py-3 border-b border-slate-200 text-sm font-semibold text-slate-900">Extracted entities</div>
        {!run.entities?.length ? (
          <div className="px-4 py-4 text-sm text-slate-500">No entities extracted.</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {run.entities
              .slice()
              .sort((a, b) => (a.order ?? 1_000) - (b.order ?? 1_000))
              .map((entity, index) => (
                <div key={`${entity.type}-${entity.name}-${index}`} className="px-4 py-3 flex items-center justify-between gap-4 text-sm">
                  <div className="min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                      {entity.name}
                      {/* The rank the brand was mentioned at. A question that names the brand puts it
                          first by construction, so for those purposes the number is the question read
                          back, not a placing. */}
                      {showsPosition ? <span className="ml-2 font-normal text-slate-400">#{entity.order}</span> : null}
                    </div>
                    <div className="text-xs text-slate-500">
                      {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('TaxonomyFacet', entity.type)} ·{' '}
                      {SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptRunEntityMatchType', entity.matchType)}
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-500 shrink-0">
                    {showsShareOfVoice ? <div>Share {(entity.shareOfVoice * 100).toFixed(1)}%</div> : null}
                    {showsSentiment ? (
                      <div>
                        {TONE_METRIC_LABEL} {toneValue(entity)?.toFixed(2) ?? '-'}
                      </div>
                    ) : null}
                    {showsRecommendation && entity.recommendation ? (
                      <span className="mt-1 inline-flex items-center rounded-md border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                        ★ Recommended
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* The same verdict the sidebar's "AI recommends" card carries, so it answers to the same KPI. */}
      {isV2 && showsRecommendation && explicitRecommendation ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-2 py-0.5 text-xs font-bold text-emerald-700">
              ★ Explicit recommendation
            </span>
            <TermRefBadge api={api} reference={explicitRecommendation.entity} />
            {parsed?.recVoteDisagreement ? (
              <span
                className="inline-flex items-center rounded-md border border-amber-300 bg-amber-50 px-2 py-0.5 text-[11px] font-bold text-amber-700"
                title="The majority-of-3 recommendation vote disagreed (audit signal)."
              >
                Vote disagreement
              </span>
            ) : null}
          </div>
          {explicitRecommendation.evidence ? (
            <blockquote className="mt-2 border-l-2 border-emerald-300 pl-3 text-sm italic text-slate-700">
              “{explicitRecommendation.evidence}”
            </blockquote>
          ) : null}
        </div>
      ) : null}

      {isV2 && qualityMetrics ? (
        <div className="rounded-xl bg-white p-4 ring-1 ring-inset ring-slate-200/80">
          <div className="mb-2 text-sm font-semibold text-slate-900">Run quality</div>
          <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Contested units</div>
              <div className="text-slate-800">{qualityMetrics.contestedUnitCount ?? '-'}</div>
            </div>
            <div>
              <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                Pending confirmation
              </div>
              <div className="text-slate-800">{formatScore(qualityMetrics.pendingConfirmationRate)}</div>
            </div>
            <div>
              <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Divergence</div>
              <div className="text-slate-800">{formatScore(qualityMetrics.divergenceRate)}</div>
            </div>
            <div>
              <div className="font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">Vote disagreement</div>
              <div className="text-slate-800">{formatScore(qualityMetrics.voteDisagreementRate)}</div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="overflow-hidden rounded-xl bg-white ring-1 ring-inset ring-slate-200/80">
        <div
          className="flex items-center gap-1 px-2 py-2 border-b border-slate-200 bg-slate-50"
          role="tablist"
          aria-label="Run parsed content tabs"
        >
          {tabs.map((tab) => {
            const isActive = currentTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  isActive
                    ? 'border-slate-300 bg-white text-slate-900'
                    : 'border-transparent bg-transparent text-slate-600 hover:border-slate-200 hover:bg-white hover:text-slate-800'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        <div className="px-4 py-4 text-sm text-slate-700">
          {parsedPayloadLoading ? <div className="text-slate-500">Loading parsed answer...</div> : null}
          {!parsedPayloadLoading && parsedPayloadError ? <div className="text-rose-600">{parsedPayloadError}</div> : null}
          {!parsedPayloadLoading && !parsedPayloadError ? (
            <>
              {currentTab === 'response' ? (
                <div role="tabpanel">
                  <MarkdownContentBox markdown={responseMarkdown} emptyText="No answer available." />
                </div>
              ) : null}

              {currentTab === 'attribution' ? (
                <div className="space-y-3" role="tabpanel">
                  {!units.length ? (
                    <div className="text-slate-500">No unit-level attribution available.</div>
                  ) : (
                    units.map((unit) => {
                      const label = labelsByUnit.get(unit.id);
                      const credits = resolutionByUnit.get(unit.id) ?? [];
                      const isDebris = !!unit.debrisClass;
                      return (
                        <div
                          key={unit.id}
                          className={`rounded-md border p-3 ${isDebris ? 'border-slate-200 bg-slate-50' : 'border-slate-200 bg-white'}`}
                        >
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-mono text-[11px] text-slate-500">{unit.id}</span>
                            <span className="inline-flex items-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
                              {prettifyEnum(unit.type)}
                            </span>
                            <span className="text-[11px] text-slate-400">{unit.wordCount} words</span>
                            {isDebris ? (
                              <span
                                className="inline-flex items-center rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-bold text-rose-600"
                                title="Excluded from credit"
                              >
                                Debris · {prettifyEnum(unit.debrisClass)}
                              </span>
                            ) : null}
                            {label?.voteDisagreement ? (
                              <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">
                                Vote disagreement
                              </span>
                            ) : null}
                          </div>

                          {label &&
                          (label.subjects?.length || label.topics?.length || label.untrackedCoSubjects?.length || label.comparison) ? (
                            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
                              {label.subjects?.length ? (
                                <span className="flex flex-wrap items-center gap-1">
                                  <span className="text-slate-400">Subjects:</span>
                                  {label.subjects.map((subject, subjectIndex) => (
                                    <TermRefBadge key={`subject-${unit.id}-${subjectIndex}`} api={api} reference={subject} />
                                  ))}
                                </span>
                              ) : null}
                              {label.topics?.length ? (
                                <span className="flex flex-wrap items-center gap-1">
                                  <span className="text-slate-400">Topics:</span>
                                  {label.topics.map((topic, topicIndex) => (
                                    <TermRefBadge key={`topic-${unit.id}-${topicIndex}`} api={api} reference={topic} />
                                  ))}
                                </span>
                              ) : null}
                              {label.untrackedCoSubjects?.length ? (
                                <span className="flex flex-wrap items-center gap-1">
                                  <span className="text-slate-400">Untracked:</span>
                                  {label.untrackedCoSubjects.map((name, nameIndex) => (
                                    <span
                                      key={`untracked-${unit.id}-${nameIndex}`}
                                      className="inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600"
                                    >
                                      {name}
                                    </span>
                                  ))}
                                </span>
                              ) : null}
                              {label.comparison ? (
                                <span className="flex items-center gap-1">
                                  <span className="text-slate-400">Comparison:</span>
                                  <span className="font-semibold text-slate-700">{prettifyEnum(label.comparison.kind)}</span>
                                  {label.comparison.counterpartUntracked ? (
                                    <span className="text-slate-500">(untracked)</span>
                                  ) : label.comparison.counterpartTerm ? (
                                    <TermRefBadge api={api} reference={label.comparison.counterpartTerm} />
                                  ) : null}
                                </span>
                              ) : null}
                            </div>
                          ) : null}

                          {credits.length ? (
                            <div className="mt-2 overflow-x-auto">
                              <table className="w-full text-xs text-slate-700 border-collapse">
                                <thead>
                                  <tr className="bg-slate-50 text-slate-600">
                                    <th className="border border-slate-200 px-2 py-1 text-left">Entity</th>
                                    <th className="border border-slate-200 px-2 py-1 text-right">Weight</th>
                                    <th className="border border-slate-200 px-2 py-1 text-left">Stance</th>
                                    <th className="border border-slate-200 px-2 py-1 text-left">Aspect</th>
                                    <th className="border border-slate-200 px-2 py-1 text-left">Evidence</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {credits.map((credit, creditIndex) => (
                                    <tr key={`credit-${unit.id}-${creditIndex}`} className="align-top">
                                      <td className="border border-slate-200 px-2 py-1">
                                        <TermRefBadge api={api} reference={credit.entity} />
                                      </td>
                                      <td className="border border-slate-200 px-2 py-1 text-right">{formatScore(credit.weight)}</td>
                                      <td className="border border-slate-200 px-2 py-1">{prettifyEnum(credit.stance)}</td>
                                      <td className="border border-slate-200 px-2 py-1">{credit.aspect || '-'}</td>
                                      <td className="border border-slate-200 px-2 py-1 whitespace-pre-wrap break-words">
                                        {credit.evidence || '-'}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                </div>
              ) : null}

              {currentTab === 'aspects' ? (
                <div className="rounded-md border border-slate-200 bg-white overflow-hidden" role="tabpanel">
                  {!aspects.length ? (
                    <div className="px-3 py-3 text-slate-500">No aspect scores available.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-slate-700 border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600">
                            <th className="border border-slate-200 px-2 py-1 text-left">Entity</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">Aspect</th>
                            <th className="border border-slate-200 px-2 py-1 text-right">Score</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">Evidence</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aspects.map((aspect, aspectIndex) => (
                            <tr key={`aspect-${aspectIndex}`} className="align-top">
                              <td className="border border-slate-200 px-2 py-1">
                                <TermRefBadge api={api} reference={aspect.entity} />
                              </td>
                              <td className="border border-slate-200 px-2 py-1">
                                <TermRefBadge api={api} reference={aspect.aspectTerm} />
                              </td>
                              <td className="border border-slate-200 px-2 py-1 text-right">{formatScore(aspect.score)}</td>
                              <td className="border border-slate-200 px-2 py-1 whitespace-pre-wrap break-words">
                                {aspect.evidence || '-'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              {currentTab === 'evidence' ? (
                <div className="rounded-md border border-slate-200 bg-white overflow-hidden" role="tabpanel">
                  {!mentions.length ? (
                    <div className="px-3 py-3 text-slate-500">No evidence available.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-slate-700 border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600">
                            <th className="border border-slate-200 px-2 py-1 text-left">#</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">Entity</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">Type</th>
                            <th className="border border-slate-200 px-2 py-1 text-right">Words</th>
                            {/* The tone KPI at mention grain, and the one reading with no verdict
                                twin: the verdict grades a subject's mentions together, so a single
                                mention carries the text's own sentiment and is labelled as such.
                                The column goes rather than filling with dashes, so the table never
                                asks the reader to read an absence as a neutral reading. */}
                            {showsSentiment ? <th className="border border-slate-200 px-2 py-1 text-right">{MENTION_TONE_LABEL}</th> : null}
                            <th className="border border-slate-200 px-2 py-1 text-left">Text</th>
                          </tr>
                        </thead>
                        <tbody>
                          {mentions
                            .slice()
                            .sort((a, b) => a.order - b.order)
                            .map((mention, index) => (
                              <tr key={`${mention.order}-${mention.entityName}-${index}`} className="align-top">
                                <td className="border border-slate-200 px-2 py-1">{mention.order + 1}</td>
                                <td className="border border-slate-200 px-2 py-1">{mention.entityName || '-'}</td>
                                <td className="border border-slate-200 px-2 py-1">{renderFacet(mention.entityType)}</td>
                                <td className="border border-slate-200 px-2 py-1 text-right">{mention.wordCount}</td>
                                {showsSentiment ? (
                                  <td className="border border-slate-200 px-2 py-1 text-right">
                                    {typeof mention.textSentiment === 'number' ? mention.textSentiment.toFixed(2) : '-'}
                                  </td>
                                ) : null}
                                <td className="border border-slate-200 px-2 py-1 whitespace-pre-wrap break-words">{mention.rawText}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}

              {currentTab === 'citations' ? (
                <div className="rounded-md border border-slate-200 bg-white overflow-hidden" role="tabpanel">
                  {!citations.length ? (
                    <div className="px-3 py-3 text-slate-500">No citations available.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs text-slate-700 border-collapse">
                        <thead>
                          <tr className="bg-slate-50 text-slate-600">
                            <th className="border border-slate-200 px-2 py-1 text-left">Domain</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">Title</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">Entity</th>
                            <th className="border border-slate-200 px-2 py-1 text-right">Mention count</th>
                            <th className="border border-slate-200 px-2 py-1 text-right">First seen #</th>
                            <th className="border border-slate-200 px-2 py-1 text-left">URL</th>
                          </tr>
                        </thead>
                        <tbody>
                          {citations.map((citation, index) => (
                            <tr key={`${citation.url}-${index}`} className="align-top">
                              <td className="border border-slate-200 px-2 py-1">{citation.domain || '-'}</td>
                              <td className="border border-slate-200 px-2 py-1">{citation.title || '-'}</td>
                              <td className="border border-slate-200 px-2 py-1">
                                {citation.entityName || '-'}
                                {citation.entityType ? <span className="text-slate-500"> ({renderFacet(citation.entityType)})</span> : null}
                              </td>
                              <td className="border border-slate-200 px-2 py-1 text-right">
                                {typeof citation.mentionCount === 'number' ? citation.mentionCount : '-'}
                              </td>
                              <td className="border border-slate-200 px-2 py-1 text-right">
                                {typeof citation.firstSeenOrder === 'number' ? citation.firstSeenOrder + 1 : '-'}
                              </td>
                              <td className="border border-slate-200 px-2 py-1 break-all">
                                <a
                                  href={citation.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-sky-700 hover:text-sky-800 underline"
                                >
                                  {citation.url}
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
